import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Grid, Typography, Chip, Divider, Box, Table, TableBody, TableRow, TableCell,
} from "@mui/material";

// Deterministic fact keys rendered as a "Label: value" grid — matches the
// shape helpers/complianceGaps.js returns for a claim or a prior-auth row.
const FACT_FIELDS = [
    ["claim_id", "Claim ID"],
    ["claim_status", "Claim Status"],
    ["prior_auth_id", "Prior Auth ID"],
    ["prior_auth", "Prior Auth #"],
    ["procedure_code", "Procedure Code"],
    ["service_start_date", "Service Start"],
    ["service_end_date", "Service End"],
    ["auth_start_date", "Auth Start"],
    ["auth_end_date", "Auth End"],
    ["units_billed", "Units Billed"],
    ["units_delivered", "Units Delivered"],
    ["units_approved", "Units Approved"],
    ["unit_of_service", "Unit of Service"],
    ["amount_billed", "Amount Billed"],
];

function verdictColor(verdict) {
    if (!verdict || verdict.justified === null || verdict.justified === undefined) return "default";
    return verdict.justified ? "success" : "error";
}

export default function ComplianceDetails({ open, onClose, record }) {
    if (!record) return null;
    const facts = record.sql_facts || {};
    const verdict = record.llm_verdict || {};
    const tasks = facts.tasks || [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {record.gap_type === "prior_auth" ? "Prior Authorization" : "Claim"} Gap
                {" — "}Customer #{record.cus_id}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Deterministic Facts
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    {FACT_FIELDS.filter(([key]) => facts[key] !== undefined && facts[key] !== null).map(([key, label]) => (
                        <Grid size={{ xs: 6, md: 4 }} key={key}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2">{String(facts[key])}</Typography>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ mb: 2 }}>
                    {(facts.issues || []).map((issue) => (
                        <Chip key={issue.code} label={issue.detail || issue.code} color="warning" size="small" sx={{ mr: 1, mb: 1 }} />
                    ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    LLM Narrative Verdict
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Chip
                        label={verdict.justified === null || verdict.justified === undefined
                            ? "No Verdict"
                            : (verdict.justified ? "Justified" : "Not Justified")}
                        color={verdictColor(verdict)}
                    />
                    {verdict.confidence && <Chip label={`Confidence: ${verdict.confidence}`} variant="outlined" size="small" />}
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {verdict.rationale || "No rationale returned."}
                </Typography>

                {tasks.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Linked Tasks ({tasks.length})
                        </Typography>
                        <Table size="small">
                            <TableBody>
                                {tasks.map((task) => (
                                    <TableRow key={task.task_id}>
                                        <TableCell>{task.task_id}</TableCell>
                                        <TableCell>{task.title || "—"}</TableCell>
                                        <TableCell>{task.service_date || "—"}</TableCell>
                                        <TableCell>{task.performed_by || "—"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={task.justified ? "Justified" : "Not Justified"}
                                                color={task.justified ? "success" : "error"}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
